/* eslint-disable @typescript-eslint/no-explicit-any */
import {DestroyRef, Injectable} from '@angular/core';
import {AlertModalService} from '../../../common/component/modal';
import {LookupService} from '../../../common/service/lookup.service';
import {AngleUtilService} from './angle/angle-util.service';
import {FeaturesApplyInsertService} from '../features-apply-insert.service';
import {FeatureAssociationService} from './feature-association.service';
import {AnnotationService} from './annotation.service';
import {AnnotationUtilityService} from './annotation-utility.service';
import {LabelExpressionService} from '../label-expression/label-expression.service';
import {PlanModelService} from '../../../common/service/plan-model.service';
import {SpatialUtilService} from '../spatial-util.service';
import {MapModeService} from '../map-mode.service';
import {SpatialSequenceService} from '../../../common/service/spatial-sequence.service';
import {PrintAreaService} from '../../../common/service/print-area.service';
import {MetaData, NgEventBus} from 'ng-event-bus';
import {AnnotationConfigLookup} from '../../../common/models/annotation-config.model';
import {EsriModuleService} from '../../esri-nlr/services/esri-module.service';
import {catchError, of, tap} from 'rxjs';
import {switchMap} from 'rxjs/operators';
import {LegalParcelTypeLookup} from '../../../common/models/legal-parcel-type.model';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';

@Injectable({
    providedIn: 'root'
})
export class RoadService {
    title = 'RoadService';

    handler: any;

    constructor(
        private alertModalService: AlertModalService,
        private angleUtilService: AngleUtilService,
        private destroyRef: DestroyRef,
        private esriModuleService: EsriModuleService,
        private eventBus: NgEventBus,
        private featuresApplyInsertService: FeaturesApplyInsertService,
        private featureAssociationService: FeatureAssociationService,
        private annotationService: AnnotationService,
        private annotationUtilService: AnnotationUtilityService,
        private labelExpressionService: LabelExpressionService,
        private lookupService: LookupService,
        private mapModeService: MapModeService,
        private planModelService: PlanModelService,
        private printAreaService: PrintAreaService,
        private spatialSequenceService: SpatialSequenceService,
        private spatialUtilService: SpatialUtilService
    ) {
        this.eventBus
            .on('removeRoadAnnotationAssociation')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                console.debug(`${this.title} - on - removeRoadAnnotationAssociation`);
                this.deleteHandler();
            });

        this.eventBus
            .on('addRoadAnnotationAssociation')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((meta: MetaData) => {
                console.debug(`${this.title} - on - addRoadAnnotationAssociation`);
                const feature = meta.data;

                const polygonIds: number[] = this.spatialUtilService.parseIdsAttr(feature.polygonids);
                if (polygonIds.length === 0) {
                    return;
                }

                const childId = polygonIds[0];
                const childFeature = this.spatialUtilService.findById('polygon', childId);

                const legalParcelTypeLookups = this.getLegalParcelTypeLookups();

                const roadLegalParcelTypeLookup = legalParcelTypeLookups.find(l => l.description === 'Road');

                const attributes = {
                    featuretype: 'road',
                    featureid: feature.id,
                    featuresubtype: roadLegalParcelTypeLookup.id,
                    guid: planModelService.getGuid(),
                    rotation: 90.0,
                    rotationdms: '90:00:00',
                    attribute9: 'road',
                    attribute10: this.annotationUtilService.newAnnotationSetGuid()
                };

                const attributesGroup = {};

                this.annotationService.getAnnotationConfigurations('road', roadLegalParcelTypeLookup.id).forEach(ac => {
                    attributes[ac.annotationAttribute] = feature[ac.featureAttribute];

                    if (ac.featureAttribute === 'polygon.statedarea' && childFeature !== undefined) {
                        attributes[ac.annotationAttribute] = childFeature.attributes.statedarea;
                    } else if (ac.lookupService !== null) {
                        const legalParcelTypeLookup = legalParcelTypeLookups.find(
                            l => l.id === childFeature.attributes.type || l.description === childFeature.attributes.type
                        );
                        if (legalParcelTypeLookup) {
                            attributes[ac.annotationAttribute] = legalParcelTypeLookup.description;
                        }
                    }
                    attributesGroup[ac.annotationAttribute] = !!ac.parent;
                });

                this.create(attributes, attributesGroup);
            });
    }

    create(attributes: any, attributesGroup: any) {
        console.debug(`${this.title} - create`);

        this.spatialUtilService.map.setMapCursor(this.spatialUtilService.getCrossCursorUrl());

        this.handler = this.spatialUtilService.map.on('click', (event: any) => {
            this.deleteHandler();
            this.createRoadAnnotation(attributes, attributesGroup, event.mapPoint).subscribe(() => {
                this.eventBus.cast('stoproadAnnotationTool');
                this.spatialUtilService.map.setMapCursor(this.spatialUtilService.getDefaultCursorUrl());
            });
        });
    }

    addRoad(nonSpatialRoads: any[]) {
        console.debug(`${this.title} - addRoad`);

        const roads = this.spatialSequenceService
            .getRoadSequence(nonSpatialRoads.length)
            .map((v, i) =>
                this.populateAttributes(
                    nonSpatialRoads[i],
                    new this.esriModuleService.Graphic(new this.esriModuleService.geometry.Polygon(), null, {id: v})
                )
            );

        return this.featuresApplyInsertService
            .insertFeaturesByType(this.spatialUtilService.getRoadMapLayer(), roads)
            .pipe(tap(() => console.debug('Roads successfully created!')));
    }

    private deleteHandler() {
        if (this.handler) {
            this.handler.remove();
            this.handler = null;
        }
    }

    private populateAttributes(nonSpatialRoad: any, spatialRoad: any) {
        console.debug(`${this.title} - populateAttributes`);

        spatialRoad.attributes.guid = this.planModelService.getGuid();
        spatialRoad.attributes.roadid = nonSpatialRoad.roadid;
        spatialRoad.attributes.type = nonSpatialRoad.type;
        spatialRoad.attributes.typecode = nonSpatialRoad.typecode;
        spatialRoad.attributes.suffix = nonSpatialRoad.suffix;
        spatialRoad.attributes.name = nonSpatialRoad.name;

        return spatialRoad;
    }

    private createRoadAnnotation(attributes: any, attributesGroup: any, mapPoint: any) {
        console.debug(`${this.title} - createRoadAnnotation`);

        const baseOffset = this.printAreaService.getReferenceScaleDivisor() * 0.004;

        const annotationsToBeCreated = [];
        const attributesOffsetMap = {};
        const annotationConfigLookups = this.getAnnotationConfigLookups();

        if (attributes.featuretype === 'road' && attributes.featuresubtype === 10) {
            Object.keys(attributesGroup).forEach(k => {
                const acLookup = annotationConfigLookups.find(
                    l =>
                        l.featureType === 'road' &&
                        l.annotationAttribute === k &&
                        l.featureSubTypes &&
                        l.featureSubTypes.split(',').includes(`${attributes.featuresubtype}`)
                );
                if (acLookup.singleAnnoOffsetDistance) {
                    attributesOffsetMap[k] = acLookup.singleAnnoOffsetDistance * this.printAreaService.getReferenceScaleDivisor();
                }
            });
        }

        let isGroup = !Object.values(attributesGroup).some((value: any) => value === false);

        for (const [groupKey, groupValue] of Object.entries(attributesGroup)) {
            if (groupValue === false || isGroup) {
                const attributeNumber = groupKey.replace(/^\D+/g, '');
                const multiplier = attributeNumber ? parseInt(attributeNumber) - 1 : 0;
                let offsetY = 0;

                if (!isGroup) {
                    offsetY = attributesOffsetMap[groupKey] || multiplier * baseOffset;
                }

                const pointGeometry = new this.esriModuleService.geometry.Point(mapPoint.x, mapPoint.y - offsetY);
                pointGeometry.setSpatialReference(mapPoint.spatialReference);

                const pointAttributes: {x: number; y: number; rotationdms?: string; rotation?: number} = {x: mapPoint.x, y: mapPoint.y};

                for (const [attributeKey, attribute] of Object.entries(attributes) as any[]) {
                    if (attribute && typeof attribute === 'object') {
                        pointAttributes[attributeKey] = attribute.id;
                    } else if (attributesGroup[attributeKey] || attributeKey === groupKey || typeof attributesGroup[attributeKey] === 'undefined') {
                        pointAttributes[attributeKey] = attribute;
                    }
                }

                if (this.mapModeService.isMapModeType('enlargement')) {
                    pointAttributes['enlargementid'] = this.mapModeService.mapMode.id;
                }

                const pointGraphic = new this.esriModuleService.Graphic(pointGeometry);
                if (pointAttributes.rotationdms) {
                    pointAttributes.rotation = this.angleUtilService.dmsToDd(pointAttributes.rotationdms);
                }
                pointGraphic.attributes = pointAttributes;
                annotationsToBeCreated.push(pointGraphic);

                isGroup = false;
            }
        }

        return this.annotationService.populateLabelExpressionAndCreateAnnotations(annotationsToBeCreated);
    }

    getRoadAnnotationConfigLookups(polygons: any[]) {
        const polygonTypeSet = new Set(polygons.map(p => String(p.attributes.featuresubtype)));
        return this.getAnnotationConfigLookups().filter(
            row =>
                row.featureType === 'road' &&
                row.featureSubTypes
                    ?.split(',')
                    .map(s => s.trim())
                    .some(type => polygonTypeSet.has(type))
        );
    }

    updateRoads(nonSpatialRoads: any[]) {
        const logPrefix = `${this.title} - updateRoads`;
        console.info(logPrefix);

        const roads: any[] = this.spatialUtilService.getRoads().filter((f: any) => nonSpatialRoads.some(r => r.roadid === f.attributes.roadid));

        console.debug(`${logPrefix} - roads:`, roads);

        if (roads.length === 0) {
            return of(null);
        }

        const nonSpatialRoadMap = nonSpatialRoads.reduce((acc, v) => {
            if (!acc[v.roadid]) {
                acc[v.roadid] = v;
            }
            return acc;
        }, {});

        const roadUpdates = roads
            .map(v => {
                const nonSpatialRoad = nonSpatialRoadMap[v.attributes.roadid];

                const name = nonSpatialRoad.name;
                const typecode = nonSpatialRoad.typecode || '';
                const suffix = nonSpatialRoad.suffix || '';

                if (v.attributes.name !== name || v.attributes.typecode !== typecode || v.attributes.suffix !== suffix) {
                    const newRoadJson = v.toJson();
                    Object.assign(newRoadJson.attributes, {name: name, typecode: typecode, suffix: suffix});
                    return new this.esriModuleService.Graphic(newRoadJson);
                }
            })
            .filter(v => v);

        if (roadUpdates.length === 0) {
            return of(null);
        }

        const roadIds = roadUpdates.map(v => v.attributes.id);

        const spatialRoadMap = roadUpdates.reduce((acc, v) => {
            if (!acc[v.attributes.id]) {
                acc[v.attributes.id] = v;
            }
            return acc;
        }, {});

        const edits: {annotation?: any[]; lot?: any[]; road: any[]} = {
            road: [null, roadUpdates, null]
        };

        const annotations: any[] = this.spatialUtilService
            .getAnnotations()
            .filter((f: any) => f.attributes.featuretype === 'road' && roadIds.includes(f.attributes.featureid));

        console.debug(`${logPrefix} - annotations`, annotations);

        if (annotations.length) {
            const annotationConfigurations = this.getRoadAnnotationConfigLookups(annotations);

            const acMap = annotationConfigurations.reduce((acc, v) => {
                if (!acc[v.annotationAttribute] && ['name', 'typecode', 'suffix'].includes(v.featureAttribute)) {
                    acc[v.annotationAttribute] = v;
                }
                return acc;
            }, {});

            const annotationsToUpdate = annotations.filter(
                v => v.attributes.featuresubtype === 10 && Object.keys(acMap).some(k => ![null, undefined].includes(v.attributes[k]))
            );

            const annotationUpdates = annotationsToUpdate.map(v => {
                const spatialRoad = spatialRoadMap[v.attributes.featureid];

                const annotationAttribute = Object.keys(acMap).find(k => ![null, undefined].includes(v.attributes[k]));
                const annotationConfiguration = acMap[annotationAttribute];

                const newAnnotationJson = v.toJson();
                newAnnotationJson.attributes[annotationAttribute] = spatialRoad.attributes[annotationConfiguration.featureAttribute];

                return new this.esriModuleService.Graphic(newAnnotationJson);
            });

            const enlargementRoadAnnotationsMap = annotationsToUpdate.reduce((acc: any, v: any) => {
                const enlargementId = v.attributes.enlargementid || 0;
                const roadId = v.attributes.featureid;
                acc[enlargementId] = acc[enlargementId] || {};
                acc[enlargementId][roadId] = acc[enlargementId][roadId] || [];
                acc[enlargementId][roadId].push(v);
                return acc;
            }, {});

            const enlargementIds = Object.keys(enlargementRoadAnnotationsMap)
                .map(k => parseInt(k))
                .sort((a, b) => a - b);

            const {adds, deletes} = enlargementIds.reduce(
                (acc, enlargementId) => {
                    const roadIds: number[] = Object.keys(enlargementRoadAnnotationsMap[enlargementId])
                        .map(k => parseInt(k))
                        .sort((a, b) => a - b);

                    roadIds.forEach(roadId => {
                        const spatialRoad = spatialRoadMap[roadId];
                        const roadAnnotations: any[] = enlargementRoadAnnotationsMap[enlargementId][roadId];

                        const attrName = Object.keys(acMap).find(k => acMap[k].featureAttribute === 'name');
                        const nameAnnotation = roadAnnotations.find(a => a.attributes[attrName]);

                        const attrTypeCode = Object.keys(acMap).find(k => acMap[k].featureAttribute === 'typecode');
                        let typeCodeAnnotation = roadAnnotations.find(a => a.attributes[attrTypeCode]);

                        if (spatialRoad.attributes.typecode) {
                            if (!typeCodeAnnotation) {
                                // Clone the "name" annotation
                                const typeCodeJson = nameAnnotation.toJson();

                                // Clear "id" and "name" attributes
                                this.spatialUtilService.deleteAttrs(typeCodeJson.attributes, ['id', attrName]);

                                // Set road "typecode" attribute
                                typeCodeJson.attributes[attrTypeCode] = spatialRoad.attributes.typecode;

                                // Assign new id
                                typeCodeJson.attributes.id = this.spatialSequenceService.getAnnotationSequence(1)[0];

                                // Assign parent id
                                typeCodeJson.attributes.parentid = null;
                                if (enlargementId) {
                                    const mainPlanTypeCodeAnnotation = acc.adds.find(
                                        a =>
                                            !a.attributes.enlargementid &&
                                            a.attributes[attrTypeCode] &&
                                            a.attributes.featureid === roadId &&
                                            a.attributes.featuretype === typeCodeJson.attributes.featuretype &&
                                            a.attributes.childtype === typeCodeJson.attributes.childtype &&
                                            a.attributes.childid === typeCodeJson.attributes.childid
                                    );
                                    if (mainPlanTypeCodeAnnotation) {
                                        typeCodeJson.attributes.parentid = mainPlanTypeCodeAnnotation.attributes.id;
                                    }
                                }

                                const nameOffset = acMap[attrName].singleAnnoOffsetDistance;
                                const typeCodeOffset = acMap[attrTypeCode].singleAnnoOffsetDistance;

                                // Offset geometry y
                                typeCodeJson.geometry.y -= (typeCodeOffset - nameOffset) * this.printAreaService.getReferenceScaleDivisor();

                                // Set label expression
                                typeCodeJson.attributes.labelexpression = this.labelExpressionService.create(typeCodeJson.attributes);

                                // Add "typecode" annotation
                                typeCodeAnnotation = new this.esriModuleService.Graphic(typeCodeJson);
                                acc.adds.push(typeCodeAnnotation);
                            }
                        } else if (typeCodeAnnotation) {
                            // Delete "typecode" annotation
                            acc.deletes.push(this.spatialUtilService.toApplyEditsDelete('annotation', typeCodeAnnotation));
                        }

                        const attrSuffix = Object.keys(acMap).find(k => acMap[k].featureAttribute === 'suffix');
                        const suffixAnnotation = roadAnnotations.find(a => a.attributes[attrSuffix]);

                        if (spatialRoad.attributes.suffix) {
                            if (!suffixAnnotation && typeCodeAnnotation) {
                                // Clone the "typecode" annotation
                                const suffixJson = typeCodeAnnotation.toJson();

                                // Clear "id", "name" and "typecode" attributes
                                this.spatialUtilService.deleteAttrs(suffixJson.attributes, ['id', attrName, attrTypeCode]);

                                // Set road "suffix" attribute
                                suffixJson.attributes[attrSuffix] = spatialRoad.attributes.suffix;

                                // Assign new id
                                suffixJson.attributes.id = this.spatialSequenceService.getAnnotationSequence(1)[0];

                                // Assign parent id
                                suffixJson.attributes.parentid = null;
                                if (enlargementId) {
                                    const mainPlanSuffixAnnotation = acc.adds.find(
                                        a =>
                                            !a.attributes.enlargementid &&
                                            a.attributes[attrSuffix] &&
                                            a.attributes.featureid === roadId &&
                                            a.attributes.featuretype === suffixJson.attributes.featuretype &&
                                            a.attributes.childtype === suffixJson.attributes.childtype &&
                                            a.attributes.childid === suffixJson.attributes.childid
                                    );
                                    if (mainPlanSuffixAnnotation) {
                                        suffixJson.attributes.parentid = mainPlanSuffixAnnotation.attributes.id;
                                    }
                                }

                                const typeCodeOffset = acMap[attrTypeCode].singleAnnoOffsetDistance;
                                const suffixOffset = acMap[attrSuffix].singleAnnoOffsetDistance;

                                // Offset geometry y
                                suffixJson.geometry.y -= (suffixOffset - typeCodeOffset) * this.printAreaService.getReferenceScaleDivisor();

                                // Set label expression
                                suffixJson.attributes.labelexpression = this.labelExpressionService.create(suffixJson.attributes);

                                // Add suffix annotation
                                acc.adds.push(new this.esriModuleService.Graphic(suffixJson));
                            }
                        } else if (suffixAnnotation) {
                            // Delete suffix annotation
                            acc.deletes.push(this.spatialUtilService.toApplyEditsDelete('annotation', suffixAnnotation));
                        }
                    });

                    return acc;
                },
                {adds: [], deletes: []}
            );

            edits.annotation = [adds.length ? adds : null, annotationUpdates, deletes.length ? deletes : null];
        }

        const lots: any[] = this.spatialUtilService.getLots().filter((f: any) => roadIds.includes(f.attributes.roadid));

        if (lots.length) {
            const lotUpdates = lots.map(v => {
                const spatialRoad = spatialRoadMap[v.attributes.roadid];
                const newLotJson = v.toJson();
                Object.assign(newLotJson.attributes, {
                    roadname: spatialRoad.attributes.name,
                    roadtype: spatialRoad.attributes.typecode || '',
                    roadsuffix: spatialRoad.attributes.suffix || ''
                });

                return new this.esriModuleService.Graphic(newLotJson);
            });

            edits.lot = [null, lotUpdates, null];

            const lotMap = lotUpdates.reduce((acc, v) => {
                if (!acc[v.attributes.id]) {
                    acc[v.attributes.id] = v;
                }
                return acc;
            }, {});

            const lotIds = lotUpdates.map(v => {
                return v.attributes.id;
            });

            const lotAnnotationConfigurations = this.annotationService.getAnnotationConfigurations('lot', '10');

            const acMap = lotAnnotationConfigurations.reduce((acc, v) => {
                if (!acc[v.annotationAttribute] && ['roadname', 'roadtype', 'roadsuffix'].includes(v.featureAttribute)) {
                    acc[v.annotationAttribute] = v;
                }
                return acc;
            }, {});

            const lotAnnotations: any[] = this.spatialUtilService
                .getAnnotations()
                .filter((f: any) => f.attributes.featuretype === 'lot' && lotIds.includes(f.attributes.featureid));

            console.debug(`${this.title} - lotAnnotations`, lotAnnotations);

            if (lotAnnotations.length) {
                const lotAnnotationsToUpdate = lotAnnotations.filter((v: any) =>
                    Object.keys(acMap).some(k => ![null, undefined].includes(v.attributes[k]))
                );

                const lotAnnotationUpdates = lotAnnotationsToUpdate.map(v => {
                    const lot = lotMap[v.attributes.featureid];

                    const annotationAttribute = Object.keys(acMap).find(k => ![null, undefined].includes(v.attributes[k]));
                    const annotationConfiguration = acMap[annotationAttribute];

                    const newAnnotationJson = v.toJson();
                    newAnnotationJson.attributes[annotationAttribute] = lot.attributes[annotationConfiguration.featureAttribute];

                    return new this.esriModuleService.Graphic(newAnnotationJson);
                });

                const enlargementLotAnnotationsMap = lotAnnotationsToUpdate.reduce((acc, v) => {
                    const enlargementId = v.attributes.enlargementid || 0;
                    const lotId = v.attributes.featureid;
                    acc[enlargementId] = acc[enlargementId] || {};
                    acc[enlargementId][lotId] = acc[enlargementId][lotId] || [];
                    acc[enlargementId][lotId].push(v);
                    return acc;
                }, {});

                const enlargementIds = Object.keys(enlargementLotAnnotationsMap)
                    .map(k => parseInt(k))
                    .sort((a, b) => a - b);

                const {adds, deletes} = enlargementIds.reduce(
                    (acc, enlargementId) => {
                        const lotIds: number[] = Object.keys(enlargementLotAnnotationsMap[enlargementId])
                            .map(k => parseInt(k))
                            .sort((a, b) => a - b);

                        lotIds.forEach(lotId => {
                            const spatialLot = lotMap[lotId];
                            const lotAnnotations: any[] = enlargementLotAnnotationsMap[enlargementId][lotId];

                            const attrRoadName = Object.keys(acMap).find(k => acMap[k].featureAttribute === 'roadname');
                            const roadNameAnnotation = lotAnnotations.find(a => a.attributes[attrRoadName]);

                            const attrRoadType = Object.keys(acMap).find(k => acMap[k].featureAttribute === 'roadtype');
                            let roadTypeAnnotation = lotAnnotations.find(a => a.attributes[attrRoadType]);

                            const attrRoadSuffix = Object.keys(acMap).find(k => acMap[k].featureAttribute === 'roadsuffix');

                            if (spatialLot.attributes.roadtype) {
                                if (!roadTypeAnnotation) {
                                    // Clone the "roadname" annotation
                                    const roadTypeJson = roadNameAnnotation.toJson();

                                    // Clear "id", "roadname" and "roadsuffix" attributes
                                    this.spatialUtilService.deleteAttrs(roadTypeJson.attributes, ['id', attrRoadName, attrRoadSuffix]);

                                    // Set "roadtype" attribute
                                    roadTypeJson.attributes[attrRoadType] = spatialLot.attributes.roadtype;

                                    // Assign new id
                                    roadTypeJson.attributes.id = this.spatialSequenceService.getAnnotationSequence(1)[0];

                                    // Assign parent id
                                    roadTypeJson.attributes.parentid = null;
                                    if (enlargementId) {
                                        const mainPlanRoadTypeAnnotation = acc.adds.find(
                                            a =>
                                                !a.attributes.enlargementid &&
                                                a.attributes[attrRoadType] &&
                                                a.attributes.featureid === lotId &&
                                                a.attributes.featuretype === roadTypeJson.attributes.featuretype &&
                                                a.attributes.childtype === roadTypeJson.attributes.childtype &&
                                                a.attributes.childid === roadTypeJson.attributes.childid
                                        );
                                        if (mainPlanRoadTypeAnnotation) {
                                            roadTypeJson.attributes.parentid = mainPlanRoadTypeAnnotation.attributes.id;
                                        }
                                    }

                                    const roadNameOffset = acMap[attrRoadName].singleAnnoOffsetDistance;
                                    const roadTypeOffset = acMap[attrRoadType].singleAnnoOffsetDistance;

                                    // Offset geometry y
                                    roadTypeJson.geometry.y -= (roadTypeOffset - roadNameOffset) * this.printAreaService.getReferenceScaleDivisor();

                                    // Set label expression
                                    roadTypeJson.attributes.labelexpression = this.labelExpressionService.create(roadTypeJson.attributes);

                                    // Add "roadtype" annotation
                                    roadTypeAnnotation = new this.esriModuleService.Graphic(roadTypeJson);
                                    acc.adds.push(roadTypeAnnotation);
                                }
                            } else if (roadTypeAnnotation) {
                                // Delete "roadtype" annotation
                                acc.deletes.push(this.spatialUtilService.toApplyEditsDelete('annotation', roadTypeAnnotation));
                            }

                            const roadSuffixAnnotation = lotAnnotations.find(a => a.attributes[attrRoadSuffix]);

                            if (spatialLot.attributes.roadsuffix) {
                                if (!roadSuffixAnnotation && roadTypeAnnotation) {
                                    // Clone the "roadtype" annotation
                                    const roadSuffixJson = roadTypeAnnotation.toJson();

                                    // Clear "id", "roadname" and "roadtype" attributes
                                    this.spatialUtilService.deleteAttrs(roadSuffixJson.attributes, ['id', attrRoadName, attrRoadType]);

                                    // Set road "suffix" attribute
                                    roadSuffixJson.attributes[attrRoadSuffix] = spatialLot.attributes.roadsuffix;

                                    // Assign new id
                                    roadSuffixJson.attributes.id = this.spatialSequenceService.getAnnotationSequence(1)[0];

                                    // Assign parent id
                                    roadSuffixJson.attributes.parentid = null;
                                    if (enlargementId) {
                                        const mainPlanRoadSuffixAnnotation = acc.adds.find(
                                            a =>
                                                !a.attributes.enlargementid &&
                                                a.attributes[attrRoadSuffix] &&
                                                a.attributes.featureid === lotId &&
                                                a.attributes.featuretype === roadSuffixJson.attributes.featuretype &&
                                                a.attributes.childtype === roadSuffixJson.attributes.childtype &&
                                                a.attributes.childid === roadSuffixJson.attributes.childid
                                        );
                                        if (mainPlanRoadSuffixAnnotation) {
                                            roadSuffixJson.attributes.parentid = mainPlanRoadSuffixAnnotation.attributes.id;
                                        }
                                    }

                                    const roadTypeOffset = acMap[attrRoadType].singleAnnoOffsetDistance;
                                    const roadSuffixOffset = acMap[attrRoadSuffix].singleAnnoOffsetDistance;

                                    // Offset geometry y
                                    roadSuffixJson.geometry.y -=
                                        (roadSuffixOffset - roadTypeOffset) * this.printAreaService.getReferenceScaleDivisor();

                                    // Set label expression
                                    roadSuffixJson.attributes.labelexpression = this.labelExpressionService.create(roadSuffixJson.attributes);

                                    // Add "roadsuffix" annotation
                                    acc.adds.push(new this.esriModuleService.Graphic(roadSuffixJson));
                                }
                            } else if (roadSuffixAnnotation) {
                                // Delete "roadsuffix" annotation
                                acc.deletes.push(this.spatialUtilService.toApplyEditsDelete('annotation', roadSuffixAnnotation));
                            }
                        });

                        return acc;
                    },
                    {adds: [], deletes: []}
                );

                if (!edits.annotation) {
                    edits.annotation = [null, null, null];
                }

                if (adds.length) {
                    edits.annotation[0] = edits.annotation[0] || [];
                    edits.annotation[0].push(...adds);
                }

                edits.annotation[1] = edits.annotation[1] || [];
                edits.annotation[1].push(...lotAnnotationUpdates);

                if (deletes.length) {
                    edits.annotation[2] = edits.annotation[2] || [];
                    edits.annotation[2].push(...deletes);
                }
            }
        }

        if (edits.annotation && edits.annotation[1] && edits.annotation[1].length) {
            const idsOfAnnotationsToUpdate = edits.annotation[1].map((v: any) => {
                return v.attributes.id;
            });

            const enlargementAnnotations: any[] = this.spatialUtilService
                .getAnnotations()
                .filter((f: any) => idsOfAnnotationsToUpdate.includes(f.attributes.parentid));

            console.debug(`${this.title} - enlargementAnnotations:`, enlargementAnnotations);

            if (enlargementAnnotations.length) {
                const annotationMap = edits.annotation[1].reduce((acc: any, v: any) => {
                    if (!acc[v.attributes.id]) {
                        acc[v.attributes.id] = v;
                    }
                    return acc;
                }, {});

                const enlargementAnnotationUpdates = enlargementAnnotations.map(v => {
                    const parentAnnotation = annotationMap[v.attributes.parentid];
                    const newEnlargementAnnoJson = v.toJson();

                    for (const [pKey, pValue] of Object.entries(parentAnnotation.attributes) as any[]) {
                        if (pKey.startsWith('attribute') && pKey !== 'attribute10' && ![null, undefined].includes(pValue)) {
                            newEnlargementAnnoJson.attributes[pKey] = pValue;
                        }
                    }

                    return new this.esriModuleService.Graphic(newEnlargementAnnoJson);
                });

                edits.annotation[1].push(...enlargementAnnotationUpdates);
            }

            edits.annotation[1].forEach((v: any) => (v.attributes.labelexpression = this.labelExpressionService.create(v.attributes).labelExpression));
        }

        console.debug(`${this.title} - edits:`, edits);

        let observable = of(null);

        if (edits.annotation) {
            observable = observable.pipe(
                switchMap(() => this.spatialUtilService.applyEdits('annotation', edits.annotation[0], edits.annotation[1], edits.annotation[2])),
                tap(res => console.debug(`${logPrefix} - annotation - applyEdits response:`, res))
            );
        }

        if (edits.lot) {
            observable = observable.pipe(
                switchMap(() => this.spatialUtilService.applyEdits('lot', edits.lot[0], edits.lot[1], edits.lot[2])),
                tap(res => console.debug(`${logPrefix} - lot - applyEdits response:`, res))
            );
        }

        if (edits.road) {
            observable = observable.pipe(
                switchMap(() => this.spatialUtilService.applyEdits('road', edits.road[0], edits.road[1], edits.road[2])),
                tap(res => console.debug(`${logPrefix} - road - applyEdits response:`, res))
            );
        }

        return observable.pipe(tap(() => this.spatialUtilService.redrawFeatureLayer('annotation')));
    }

    deleteRoads(roadsToDelete: any[]) {
        const logPrefix = `${this.title} - deleteRoads`;
        console.info(logPrefix);

        const roads: any[] = this.spatialUtilService.getRoads().filter((f: any) => roadsToDelete.some(r => r.roadid === f.attributes.roadid));

        console.debug(`${logPrefix} - roads:`, roads);

        if (roads.length === 0) {
            return of(null);
        }

        const edits: {annotation?: any[]; lot?: any[]; polygon?: any[]; road: any[]} = {
            road: [null, null, roads]
        };

        const roadIds = roads.map(v => v.attributes.id);

        const lotIds = [];

        const roadAnnotations = this.spatialUtilService
            .getAnnotations()
            .filter((f: any) => roadIds.includes(f.attributes.featureid) && f.attributes.featuretype === 'road');

        console.debug(`${logPrefix} - road annotations:`, roadAnnotations);

        if (roadAnnotations.length) {
            edits.annotation = [null, null, roadAnnotations];
        }

        const lots: any[] = this.spatialUtilService.getLots().filter((f: any) => roadIds.includes(f.attributes.roadid));

        if (lots.length) {
            const lotUpdates = lots.map(v => {
                const newPolygonJson = v.toJson();

                newPolygonJson.attributes.roadid = null;
                newPolygonJson.attributes.roadname = null;
                newPolygonJson.attributes.roadtype = null;
                newPolygonJson.attributes.roadsuffix = null;
                newPolygonJson.attributes.polygonids = null;

                return new this.esriModuleService.Graphic(newPolygonJson);
            });

            edits.lot = [null, lotUpdates, null];

            const lotIds = lotUpdates.map(v => v.attributes.id);

            const responses: AnnotationConfigLookup[][] = lotUpdates.map(v =>
                this.annotationService.getAnnotationConfigurations('lot', v.attributes.type)
            );

            const acMap = responses.reduce((acc, annotationConfigurations) => {
                annotationConfigurations.forEach(v => {
                    if (!acc[v.id] && ['roadname', 'roadtype', 'roadsuffix'].includes(v.featureAttribute)) {
                        acc[v.id] = v;
                    }
                });
                return acc;
            }, {});

            const lotAnnotations: any[] = this.spatialUtilService
                .getAnnotations()
                .filter((f: any) => lotIds.includes(f.attributes.featureid) && f.attributes.featuretype === 'lot');

            console.debug(`${logPrefix} - lot annotations:`, lotAnnotations);

            if (lotAnnotations.length) {
                const lotRoadAnnotationAttributeNames = Object.keys(acMap).reduce((acc, k) => {
                    const annotationAttribute = acMap[k].annotationAttribute;
                    if (annotationAttribute && !acc.includes(annotationAttribute)) {
                        acc.push(annotationAttribute);
                    }
                    return acc;
                }, []);

                const annotationUpdates = lotAnnotations.map(v => {
                    const newAnnotationJson = v.toJson();

                    newAnnotationJson.attributes.labelexpression = null;
                    lotRoadAnnotationAttributeNames.forEach(attrName => {
                        if (typeof newAnnotationJson.attributes[attrName] === 'string') {
                            newAnnotationJson.attributes[attrName] = '';
                        }
                    });

                    return new this.esriModuleService.Graphic(newAnnotationJson);
                });
                if (edits.annotation) {
                    if (edits.annotation[1]) {
                        edits.annotation[1].push(...annotationUpdates);
                    } else {
                        edits.annotation[1] = annotationUpdates;
                    }
                } else {
                    edits.annotation = [null, annotationUpdates, null];
                }
            }
        }

        const polygons: any[] = this.spatialUtilService
            .getPolygons()
            .filter((f: any) => roadIds.includes(f.attributes.roadid) || (lotIds && lotIds.includes(f.attributes.lotid)));

        console.debug(`${logPrefix} - polygons:`, polygons);

        if (polygons.length) {
            const polygonUpdates = polygons.map(v => {
                const newPolygonJson = v.toJson();
                newPolygonJson.attributes.roadid = null;
                newPolygonJson.attributes.lotid = null;

                return new this.esriModuleService.Graphic(newPolygonJson);
            });

            edits.polygon = [null, polygonUpdates, null];
        }

        if (edits.annotation && edits.annotation[2] && edits.annotation[2].length) {
            const annotationsToDelete = edits.annotation[2];
            const idsOfAnnotationsToDelete = annotationsToDelete.map((v: any) => v.attributes.id);

            const enlargementAnnotationsToDelete = this.spatialUtilService
                .getAnnotations()
                .filter((f: any) => idsOfAnnotationsToDelete.includes(f.attributes.parentid));

            console.debug(`${logPrefix} - enlargement annotations to delete:`, enlargementAnnotationsToDelete);

            annotationsToDelete.push(...enlargementAnnotationsToDelete);
        }

        console.debug(`${logPrefix} - edits:`, edits);

        let observable = of(null);

        if (edits.annotation) {
            observable = observable.pipe(
                switchMap(() => this.spatialUtilService.applyEdits('annotation', edits.annotation[0], edits.annotation[1], edits.annotation[2])),
                tap(res => console.debug(`${logPrefix} - annotation - applyEdits response:`, res))
            );
        }

        if (edits.polygon) {
            observable = observable.pipe(
                switchMap(() => this.spatialUtilService.applyEdits('polygon', edits.polygon[0], edits.polygon[1], edits.polygon[2])),
                tap(res => console.debug(`${logPrefix} - polygon - applyEdits response:`, res))
            );
        }

        if (edits.lot) {
            observable = observable.pipe(
                switchMap(() => this.spatialUtilService.applyEdits('lot', edits.lot[0], edits.lot[1], edits.lot[2])),
                tap(res => console.debug(`${logPrefix} - lot - applyEdits response:`, res))
            );
        }

        if (edits.road) {
            observable = observable.pipe(
                switchMap(() => this.spatialUtilService.applyEdits('road', edits.road[0], edits.road[1], edits.road[2])),
                tap(res => console.debug(`${logPrefix} - road - applyEdits response:`, res))
            );
        }

        return observable;
    }

    unlinkAndUpdateRoads(roadFeature: any, annotationChildId: any) {
        console.debug(`${this.title} - unlinkAndUpdateRoads`);

        const roadAndRoadWideningLegalParcelTypeLookupIds = this.getLegalParcelTypeLookups()
            .filter(l => ['Road', 'Road Widening'].includes(l.description))
            .map(l => l.id);

        const polygonIds: number[] = this.spatialUtilService.parseIdsAttr(roadFeature.attributes.polygonids);

        const polygonUpdates = polygonIds.length
            ? this.spatialUtilService
                  .getPolygons()
                  .filter(
                      (p: any) =>
                          polygonIds.includes(p.attributes.id) &&
                          p.attributes.id === annotationChildId &&
                          roadAndRoadWideningLegalParcelTypeLookupIds.includes(p.attributes.type)
                  )
                  .map((p: any) => {
                      const polygonUpdate = this.spatialUtilService.copyGraphic(p);
                      polygonUpdate.attributes.roadid = null;
                      return polygonUpdate;
                  })
            : [];

        const polygonIdsToKeep = polygonIds.filter(id => !polygonUpdates.some((p: any) => p.attributes.id === id));
        const polygonsToKeep = this.spatialUtilService
            .getPolygons()
            .filter((p: any) => polygonIdsToKeep.includes(p.attributes.id))
            .map((p: any) => this.spatialUtilService.copyGraphic(p));

        let observable = of(null);

        if (polygonUpdates.length) {
            if (roadFeature.attributes.type === 3) {
                this.featureAssociationService.setTotalAreaAndGeometry(roadFeature, polygonsToKeep);
            }
            observable = observable.pipe(
                switchMap(() => this.spatialUtilService.applyEdits('polygon', null, polygonUpdates, null)),
                tap((res: any) => {
                    console.debug(`${this.title} - unlinkAndUpdateRoads updates:`, res.updates);
                    if (res.updates[0].success) {
                        console.debug('Road Feature polygons have been successfully updated!');
                    }
                }),
                catchError(err => this.alertModalService.createAlertMessage(`Error updating polygon features. Error : ${err}`, 'Ok'))
            );
        }

        roadFeature.attributes.polygonids = this.spatialUtilService.toIdsAttr(polygonIdsToKeep);
        roadFeature.geometry = new this.esriModuleService.geometry.Polygon();
        polygonsToKeep.forEach((p: any) => p.geometry.rings.forEach((ring: any) => roadFeature.geometry.addRing(ring)));

        return observable.pipe(
            switchMap(() => this.spatialUtilService.applyEdits('road', null, [roadFeature], null)),
            tap((res: any) => {
                console.debug(`${this.title} - unlinkAndUpdateRoads updates:`, res.updates);
                if (res.updates[0].success) {
                    console.debug('Road Feature has been successfully updated!');
                }
            }),
            catchError(err => this.alertModalService.createAlertMessage(`Error updating road feature. Error : ${err}`, 'Ok'))
        );
    }

    private getAnnotationConfigLookups(): AnnotationConfigLookup[] {
        return this.lookupService.getLookups('annotationConfig');
    }

    private getLegalParcelTypeLookups(): LegalParcelTypeLookup[] {
        return this.lookupService.getLookups('legalParcelType');
    }
}
